/**
 * ReviewForm
 * Form for submitting or editing reviews
 * Protected actions with auth validation
 */

import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { StarRating } from '@shared/components/ui/star-rating'
import { Textarea } from '@shared/components/ui/textarea'
import { useProtectedAction } from '@shared/hooks'
import { Calendar, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useCreateReview } from '../hooks/useReviews'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Types
// ============================================================================

interface ReviewFormProps {
  professionalId: string
  onSuccess?: () => void
  onCancel?: () => void
}

// ============================================================================
// Component
// ============================================================================

export function ReviewForm({
  professionalId,
  onSuccess,
  onCancel,
}: ReviewFormProps) {
  const [rating, setRating] = useState(5)
  const [comment, setComment] = useState('')
  const [jobDate, setJobDate] = useState(
    new Date().toISOString().split('T')[0]
  )

  const createReview = useCreateReview()
  const { executeProtected, isValidating } = useProtectedAction()

  const isSubmitting = createReview.isPending || isValidating

  const handleDateClick = (e: React.MouseEvent<HTMLInputElement>) => {
    try {
      const input = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
      if (input.showPicker) {
        input.showPicker();
      }
    } catch {
      // Fallback: just focus the input
      e.currentTarget.focus();
    }
  };
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await executeProtected(async () => {
        await createReview.mutateAsync({
          professionalId,
          rating,
          comment: comment.trim() || undefined,
          jobDate: jobDate || undefined,
        })
      })
      onSuccess?.()
    } catch (error) {
      logger.error('Failed to submit review:', error)
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
      {/* Rating */}
      <div className="space-y-2">
        <Label>Calificación *</Label>
        <div className="flex items-center gap-4">
          <StarRating
            rating={rating}
            onChange={setRating}
            size="lg"
            showValue={false}
          />
          <span className="text-2xl font-semibold">{rating}.0</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Selecciona de 1 a 5 estrellas
        </p>
      </div>

      {/* Comment */}
      <div className="space-y-2 bg-">
        <Label htmlFor="comment">Comentario (Opcional)</Label>
        <Textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Cuéntanos sobre tu experiencia con este profesional..."
          rows={5}
          maxLength={500}
          disabled={isSubmitting}
          className='resize-none bg-input border-2 border-border rounded-md'
        />
        <p className="text-xs text-muted-foreground text-right">
          {comment.length}/500 caracteres
        </p>
      </div>

      {/* Job Date */}
      <div className="relative space-y-2">
        <Label htmlFor="jobDate">Fecha del Trabajo (Opcional)</Label>
        <Input
          id="jobDate"
          type="date"
          value={jobDate}
          onChange={(e) => setJobDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]} // Can't select future dates
          disabled={isSubmitting}
          onClick={handleDateClick}
          className='bg-input border-2 border-border rounded-md [&::-webkit-calendar-picker-indicator]:hidden'
        >
        </Input>
        <Calendar className="absolute right-3 top-3 transform translate-y-5 w-5 h-5 text-muted-foreground pointer-events-none" />
      </div>

      {/* Form Actions */}
      <div className="flex gap-4 pt-4">
        <Button type="submit" disabled={isSubmitting} className="flex-1">
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isSubmitting
            ? 'Enviando...'
            : 'Publicar Reseña'}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
        )}
      </div>

      {/* Success/Error Messages */}
      {createReview.isSuccess && (
        <p className="text-sm text-green-600">
          ¡Reseña publicada correctamente!
        </p>
      )}
      {createReview.isError && (
        <p className="text-sm text-destructive">
          Error al publicar la reseña. Por favor, intenta de nuevo.
        </p>
      )}
    </form>
  )
}
